import { CheckCircle, ExpandMore } from '@mui/icons-material';
import { alpha, Box, Link, MenuItem, Select, Stack, styled, Tooltip, Typography, useTheme } from '@mui/material';
import { get, isNull, truncate } from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TypePlanPrice } from 'src/utils/constants/enum';
import { AuthRouteKeys, NoAuthRouteKeys } from 'src/utils/constants/routes';
import { handleQueryParamFromObject } from 'src/utils/helpers/queryParam';
import {
  getStringOrDefault,
  renderNumberOrDefault,
  renderPriceOrDefault,
  renderTextOrDefault,
} from 'src/utils/helpers/stringHelpers';

// Utilities
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import { CreditCoinIcon } from 'src/components/Icons';
import PopupChangePlan from 'src/features/Pricing/PopupChangePlan';
import PopupConfirmGetTrial from 'src/features/Pricing/PopupConfirmGetTrial';
import PopupLeaveCurrentPlan from 'src/features/Pricing/PopupLeaveCurrentPlan';
import { useNotification } from 'src/hooks/useNotification';
import { useQueryParams } from 'src/hooks/useQueryParam';
import useRefetch from 'src/hooks/useRefetch';
import useTeamMember from 'src/hooks/useTeamMember';
import { UsageInformation } from 'src/model/payment';
import { useMutateCalculatePricePlan, useMutateSubscriptionFreeTrial } from 'src/services/mutate';
import { QUERY_KEY_USAGE } from 'src/services/queries';
import { AFFILIATE_PROGRAM_ID } from 'src/utils/constants/config';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { getCookie } from 'src/utils/helpers/cookies';
import { IPlanDetail } from 'src/utils/typings/response';

const StyledBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: '100px',
  backgroundColor: alpha(theme.palette.primary.main, 0.07),
  '.MuiSelect-select': {
    padding: '8px 12px',
    color: theme.palette.primary.main,
    fontSize: 12,
  },
  '.MuiSelect-icon': {
    color: theme.palette.primary.main,
  },
  '&:before': {
    borderBottom: 'none',
  },
}));

const PlanCard = ({
  planDetail,
  campaignSlug = '',
  useInPublicPage = false,
  dataUsage,
}: {
  planDetail: IPlanDetail;
  campaignSlug?: string;
  useInPublicPage?: boolean;
  dataUsage?: UsageInformation;
}) => {
  const theme = useTheme();
  const query = useQueryParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notification } = useNotification();
  const { refetchQuery } = useRefetch();
  const { mutateAsync, isPending } = useMutateCalculatePricePlan();
  const { mutateAsync: mutateFreeTrial, isPending: isPendingFreeTrial } = useMutateSubscriptionFreeTrial();
  const isExistedPlan = dataUsage?.status_code === SUCCESS_CODE.GET && dataUsage?.data?.status === 'active';
  const isPlanFreeTrial = planDetail.is_trial;
  const affiliateCode = getCookie(AFFILIATE_PROGRAM_ID + '_affiliate_referral');

  const [openConfirmTrial, setOpenConfirmTrial] = useState<boolean>(false);
  const [openLeavePlan, setOpenLeavePlan] = useState<boolean>(false);

  const [paramsChangePlan, setParamsChangePlan] = useState<{
    priceDifference: number;
    open: boolean;
    priceId: string;
  }>({
    priceDifference: 0,
    open: false,
    priceId: '',
  });

  const SPECIAL_PLAN_FREEMIUM = 188;

  const defaultType = () => {
    if (planDetail?.price_monthly) {
      return TypePlanPrice.Month;
    }

    if (planDetail?.price_quarterly) {
      return TypePlanPrice.Quarter;
    }

    if (planDetail?.price_half_yearly) {
      return TypePlanPrice.HalfYear;
    }

    if (planDetail?.price_yearly) {
      return TypePlanPrice.Year;
    }

    return null;
  };

  const [type, setType] = useState<TypePlanPrice | null>(defaultType());
  const { isTeamMember } = useTeamMember();

  const creditPointPerMonth = useMemo(() => {
    if (type === TypePlanPrice.Year) {
      return planDetail.credit_points_per_month_yearly || planDetail.credit_points_per_month || 0;
    }
    return planDetail.credit_points_per_month || 0;
  }, [type]);

  const displayBenefits = useMemo(() => {
    if (type === TypePlanPrice.Year && planDetail.benefits_yearly?.length) {
      return planDetail.benefits_yearly;
    }
    return planDetail.benefits || [];
  }, [type, planDetail.benefits, planDetail.benefits_yearly]);

  const shouldStartNewSubscription = () => {
    // If the user is currently on a trial plan OR has no existing plan, redirect to checkout.
    const isFreeTrial = (isExistedPlan && dataUsage?.data?.is_trial) || !isExistedPlan;

    // If the user has an existing paid plan and has canceled it, redirect to checkout.
    const isPaidAndCanceled = isExistedPlan && dataUsage?.data?.status === 'canceled';

    return isFreeTrial || isPaidAndCanceled;
  };

  const redirectCheckout = async () => {
    // If the user is a member of a team, they cannot switch plans directly. They must leave the current team first.
    if (isTeamMember) {
      setOpenLeavePlan(true);
      return;
    }

    if (isPlanFreeTrial) {
      const responseSubscription = await mutateFreeTrial({
        planId: planDetail.id,
        affiliateCode,
        campaignSlug,
      });
      notification(responseSubscription);
      if (responseSubscription?.status_code === SUCCESS_CODE.GET) {
        refetchQuery([QUERY_KEY_USAGE]);
      }
      return;
    }

    // If the user is currently on a trial plan OR has no existing plan, redirect to checkout.
    if (shouldStartNewSubscription()) {
      const params = handleQueryParamFromObject({
        planId: planDetail.id,
        campaignSlug: campaignSlug,
        type,
      });
      navigate({ pathname: AuthRouteKeys.Checkout, search: params });
      return;
    }

    // If the user has an existing paid plan and has not canceled it, calculate the price difference and show the change plan popup.
    const responseGetPrice = await mutateAsync({ priceId: get(planDetail, `stripe_price_id_${type}`, '') });
    if (responseGetPrice?.status_code === SUCCESS_CODE.GET) {
      setParamsChangePlan({
        priceDifference: responseGetPrice?.data?.price_difference || 0,
        open: true,
        priceId: get(planDetail, `stripe_price_id_${type}`, ''),
      });
    }
  };

  const handleChoosePlan = () => {
    if (useInPublicPage) {
      const params = handleQueryParamFromObject({ ...query, campaign_slug: campaignSlug });
      window.open(`${NoAuthRouteKeys.Login}?${params}`, '_blank', 'noopener,noreferrer');
    } else {
      redirectCheckout();
    }
  };

  return (
    <StyledBox border={`1px solid ${theme.palette.divider}`} borderRadius="12px" height="100%">
      <PopupChangePlan
        priceDifference={paramsChangePlan.priceDifference}
        open={paramsChangePlan.open}
        priceId={paramsChangePlan.priceId}
        handleClose={() =>
          setParamsChangePlan({
            priceDifference: 0,
            open: false,
            priceId: '',
          })
        }
      />
      <PopupConfirmGetTrial
        open={openConfirmTrial}
        planId={planDetail.id}
        creditPoints={planDetail.credit_points_per_month || 0}
        campaignSlug={campaignSlug}
        affiliateCode={affiliateCode}
        handleClose={() => setOpenConfirmTrial(false)}
      />
      <PopupLeaveCurrentPlan open={openLeavePlan} handleClose={() => setOpenLeavePlan(false)} />
      <Tooltip title={renderTextOrDefault(planDetail.plan_name)} placement="top">
        <Typography
          variant="h6"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: '1',
            lineClamp: '1',
            WebkitBoxOrient: 'vertical',
          }}
        >
          {renderTextOrDefault(planDetail.plan_name)}
        </Typography>
      </Tooltip>
      <Box mt={1} />
      <Tooltip title={renderTextOrDefault(planDetail.short_description)} placement="top">
        <Typography
          variant="body2"
          color={'textTertiary.main'}
          sx={{
            height: '60px',
            overflow: 'hidden',
          }}
        >
          {truncate(getStringOrDefault(planDetail.short_description), {
            length: 180,
          })}
        </Typography>
      </Tooltip>
      <Stack my={2} spacing={3} direction="row" alignItems="center" justifyContent="space-between">
        {!isPlanFreeTrial && !isNull(type) && (
          <StyledSelect
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            variant="filled"
            value={type}
            IconComponent={ExpandMore}
            onChange={(event) => {
              setType(event?.target?.value as TypePlanPrice);
            }}
          >
            {planDetail?.price_monthly && (
              <MenuItem value={TypePlanPrice.Month}>{t('pricing.plan_type.monthly')}</MenuItem>
            )}
            {planDetail?.price_quarterly && (
              <MenuItem value={TypePlanPrice.Quarter}>{t('pricing.plan_type.quarterly')}</MenuItem>
            )}
            {planDetail?.price_half_yearly && (
              <MenuItem value={TypePlanPrice.HalfYear}>{t('pricing.plan_type.half_yearly')}</MenuItem>
            )}
            {planDetail?.price_yearly && (
              <MenuItem value={TypePlanPrice.Year}>{t('pricing.plan_type.yearly')}</MenuItem>
            )}
          </StyledSelect>
        )}
        {!isPlanFreeTrial && !isNull(type) && (
          <Typography variant="h5">
            ${renderPriceOrDefault(get(planDetail, `price_${type}`, 0))}{' '}
            <Typography variant="body2" component="span">
              per {t(`pricing.plan_time.${type}`)}
            </Typography>
          </Typography>
        )}
        {isPlanFreeTrial && <Typography variant="h6">Free — Evaluation Access</Typography>}
      </Stack>
      {planDetail?.id !== SPECIAL_PLAN_FREEMIUM && ( //  FEAT: 86ewr2k92 - Special setup for Freemium Plans
        <Stack mb={2} spacing={1} direction="row" alignItems="center">
          <CreditCoinIcon width={20} />
          <Typography variant="subtitle2">
            {renderNumberOrDefault(creditPointPerMonth)} {t('pricing.credit')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            / {t('pricing.plan_time.monthly')}
          </Typography>
        </Stack>
      )}
      <ButtonWithLoading
        disabled={isPending || isPendingFreeTrial || (isExistedPlan && isPlanFreeTrial)}
        isLoading={isPending || isPendingFreeTrial}
        fullWidth
        color="primary"
        variant="contained"
        onClick={() => handleChoosePlan()}
      >
        {/* FEAT: 86ewr2k92 - Special setup for Freemium Plans  */}
        {planDetail?.id === SPECIAL_PLAN_FREEMIUM
          ? 'Activate Starter Access'
          : isExistedPlan
            ? t('pricing.change_plan')
            : t('pricing.choose_plan')}
      </ButtonWithLoading>

      {/* FEAT: 86ewr2k92 - Special setup for Freemium Plans  */}
      {planDetail?.id !== SPECIAL_PLAN_FREEMIUM && (
        <Typography my={1} fontSize={12} color={'text.secondary'}>
          Examples of Usage - See features here:{' '}
          <Link target="_blank" href="https://adamftd.com/en/pricing" underline="none">
            https://adamftd.com/en/pricing
          </Link>
        </Typography>
      )}

      <Stack spacing={1.5} direction="column" mt={2}>
        {displayBenefits.map((benefit, index) => (
          <Stack key={`benefit-${planDetail.id}-${index}`} spacing={1} direction="row">
            <CheckCircle color="success" fontSize="small" />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                width: '90%',
                overflowWrap: 'break-word',
              }}
            >
              {benefit}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </StyledBox>
  );
};

export default PlanCard;
